import React, { useState } from 'react'
import {
  PlusIcon,
  FileTextIcon,
  ClockIcon,
  RefreshCwIcon,
  PlayIcon,
  TrashIcon,
  AlertCircleIcon,
  XIcon,
} from 'lucide-react'
import { Document } from '../types'
import { formatFileSize, formatDate } from '../utils/helpers'
import { analyzeDocument, deleteDocument } from '../utils/api'

interface DocumentListProps {
  documents: Document[]
  error?: string | null
  onSelectDocument: (document: Document) => void
  onUploadNew: () => void
  onDocumentUpdated?: () => void
  onRetry?: () => void
  showStudentInfo?: boolean
}

export const DocumentList = ({
  documents,
  error,
  onSelectDocument,
  onUploadNew,
  onDocumentUpdated,
  onRetry,
  showStudentInfo = false,
}: DocumentListProps) => {
  const [analyzingDocs, setAnalyzingDocs] = useState<Set<string>>(new Set())
  const [deletingDocs, setDeletingDocs] = useState<Set<string>>(new Set())
  const [showErrorBanner, setShowErrorBanner] = useState(true)

  const handleAnalyzeDocument = async (document: Document, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent triggering onSelectDocument
    
    try {
      setAnalyzingDocs(prev => new Set(prev).add(document.id))
      await analyzeDocument(document.id)
      
      // Refresh the document list to update status
      if (onDocumentUpdated) {
        onDocumentUpdated()
      }
    } catch (error) {
      console.error('Failed to analyze document:', error)
    } finally {
      setAnalyzingDocs(prev => {
        const updated = new Set(prev)
        updated.delete(document.id)
        return updated
      })
    }
  }

  const handleDeleteDocument = async (document: Document, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent triggering onSelectDocument
    
    const confirmMessage = `Are you sure you want to delete "${document.original_filename}"?\n\nThis will permanently delete:\n• The document file\n• Analysis results\n• All associated data\n\nThis action cannot be undone.`
    
    if (!window.confirm(confirmMessage)) {
      return
    }
    
    try {
      setDeletingDocs(prev => new Set(prev).add(document.id))
      await deleteDocument(document.id)
      
      // Refresh the document list to remove deleted document
      if (onDocumentUpdated) {
        onDocumentUpdated()
      }
    } catch (error: any) {
      console.error('Failed to delete document:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete document. Please try again.'
      alert(`Error: ${errorMessage}`)
    } finally {
      setDeletingDocs(prev => {
        const updated = new Set(prev)
        updated.delete(document.id)
        return updated
      })
    }
  }
  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Your Documents</h1>
        <button
          onClick={onUploadNew}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Upload New Document
        </button>
      </div>

      {/* Error Banner */}
      {error && showErrorBanner && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircleIcon className="h-5 w-5 text-red-600 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Error loading documents
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {error}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                  <RefreshCwIcon className="h-4 w-4 mr-1" />
                  Try Again
                </button>
              )}
              <button
                onClick={() => setShowErrorBanner(false)}
                className="p-1 rounded-md hover:bg-red-200 text-red-600 hover:text-red-800 transition-colors"
                title="Dismiss error"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No documents yet
          </h3>
          <p className="text-gray-500 mb-4">
            Upload your first IEP document to get started
          </p>
          <button
            onClick={onUploadNew}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Upload Document
          </button>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {documents.map((document) => {
              const isAnalyzing = analyzingDocs.has(document.id)
              const isDeleting = deletingDocs.has(document.id)
              const canAnalyze = document.processing_status !== 'processing' && !isAnalyzing && !isDeleting
              const canDelete = !isAnalyzing && !isDeleting
              
              return (
                <li key={document.id}>
                  <div className="block hover:bg-gray-50 w-full">
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div 
                          className="flex items-center flex-1 cursor-pointer"
                          onClick={() => onSelectDocument(document)}
                        >
                          <FileTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <p className="text-sm font-medium text-blue-600 truncate">
                            {document.original_filename}
                          </p>
                        </div>
                        <div className="ml-2 flex-shrink-0 flex items-center space-x-2">
                          {/* Analysis button */}
                          {canAnalyze && (
                            <button
                              onClick={(e) => handleAnalyzeDocument(document, e)}
                              className="p-1 rounded-full hover:bg-blue-100 text-blue-600 hover:text-blue-700 transition-colors"
                              title={document.processing_status === 'completed' ? 'Re-analyze document' : 'Start analysis'}
                            >
                              {document.processing_status === 'completed' ? (
                                <RefreshCwIcon className="h-4 w-4" />
                              ) : (
                                <PlayIcon className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {/* Delete button */}
                          {canDelete && (
                            <button
                              onClick={(e) => handleDeleteDocument(document, e)}
                              className="p-1 rounded-full hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors"
                              title="Delete document"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {isAnalyzing && (
                            <div className="flex items-center">
                              <RefreshCwIcon className="h-4 w-4 text-blue-600 animate-spin" />
                            </div>
                          )}

                          {isDeleting && (
                            <div className="flex items-center">
                              <TrashIcon className="h-4 w-4 text-red-600 animate-pulse" />
                            </div>
                          )}
                          
                          {/* Status badge */}
                          {document.processing_status === 'uploaded' && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Uploaded
                            </span>
                          )}
                          {(document.processing_status === 'processing' || isAnalyzing) && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              {isAnalyzing ? 'Analyzing...' : 'Processing'}
                            </span>
                          )}
                          {document.processing_status === 'completed' && !isAnalyzing && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Completed
                            </span>
                          )}
                          {document.processing_status === 'error' && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Error
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                        {showStudentInfo && document.students && document.students.length > 0 ? (
                          <p className="flex items-center text-sm text-gray-500 mr-6">
                            <span className="truncate">
                              Students: {document.students.map(s => `${s.first_name} ${s.last_name}`).join(', ')}
                            </span>
                          </p>
                        ) : document.student_name_detected ? (
                          <p className="flex items-center text-sm text-gray-500 mr-6">
                            <span className="truncate">
                              Student: {document.student_name_detected}
                            </span>
                          </p>
                        ) : showStudentInfo ? (
                          <p className="flex items-center text-sm text-gray-500 mr-6">
                            <span className="truncate text-gray-400">
                              No students linked
                            </span>
                          </p>
                        ) : null}
                          <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                            <span>{formatFileSize(document.file_size)}</span>
                          </p>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          <p>
                            Uploaded on{' '}
                            <time dateTime={document.upload_date}>
                              {formatDate(document.upload_date)}
                            </time>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}